
import Vue from 'vue';
import EventService from '@/services/EventService';
import {TraineeRuntime, ContractChartRes} from '@/services/types';
import type {User} from 'firebase/auth';
import moment from 'moment';
import {saveAs} from 'file-saver';

type Data = {
  start: string;
  end: string;
  threshold: number;
  infos: Array<TraineeRuntime>;
  isLoading: boolean;
  snackbar: boolean;
  snackbarMessage: string;
  snackbarColor: string;
};

const defaultThreshold = 60;

export default Vue.extend({
  props: {
    year: {
      type: String,
      default: '',
    },
    main: {
      type: Boolean,
      default: true,
    },
  },
  async mounted() {
    if (this.user) {
      try {
        const token = await this.user().getIdToken();
        const {data} = await EventService.getYearInfo(token, Number(this.year));
        this.start = data.dates.fiscalYearStart;
        const sydStart = moment(this.start);
        this.end = sydStart.add(1, 'y').subtract(1, 'd').format('YYYY-MM-DD');

        this.infos = await this.postChart(token, this.start, this.end);
      } catch (e) {
        console.log(e);
        return;
      }
    }
  },
  computed: {
    totalRuntime(): number {
      const sum = this.infos.reduce((sum, info): number => {
        if (info.totalRuntime > this.threshold * 60) {
          return sum + this.runtimeFloor(info.totalRuntime);
        }
        return sum;
      }, 0);
      return sum;
    },
  },
  data: (): Data => {
    return {
      start: '',
      end: '',
      threshold: defaultThreshold,
      infos: [],
      isLoading: false,
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: '',
    };
  },
  methods: {
    // 2桁表示 1桁の場合は0を付与
    pad2(value) {
      return value.toString().padStart(2, '0');
    },
    // 切り捨てで表示
    runtimeFloor(base: number): number {
      return Math.floor(base / 60);
    },
    user(): User | undefined {
      return (this.$store.state as {user: User | undefined}).user;
    },

    async onInputChange() {
      const startDate = moment(this.start, 'YYYY-MM-DD');
      const endDate = moment(this.end, 'YYYY-MM-DD');
      if (startDate.isAfter(endDate)) {
        this.end = startDate.format('YYYY-MM-DD');
        return; // this.end に新しい値を入れたときにもう一度 onInputChangeが呼ばれるので APIが２回呼ばれるのを防止するために処理を抜ける
      }

      if (this.user()) {
        try {
          const token = await this.user().getIdToken();
          const infos = await this.postChart(token, this.start, this.end);
          this.infos = infos;
        } catch (e) {
          console.log(e);
          return;
        }
      }
    },

    async onDownload() {
      if (this.user()) {
        try {
          this.isLoading = true;
          const token = await this.user().getIdToken();
          await this.postChartDownload(token, this.start, this.end);
          this.snackbarMessage = 'ダウンロードが成功しました';
          this.snackbarColor = 'success';
          this.snackbar = true;
        } catch (error) {
          console.log(error);
          this.snackbarMessage = 'ダウンロードが失敗しました';
          this.snackbarColor = 'error';
          this.snackbar = true;
        } finally {
          this.isLoading = false;
        }
      }
    },

    async postChart(
      token: string,
      start: string,
      end: string
    ): Promise<Array<TraineeRuntime>> {
      const res = await EventService.postContractChart(token, {
        startDate: start,
        endDate: end,
        threshold: this.threshold,
      });
      return res.data.runtimes;
    },

    async postChartDownload(
      token: string,
      start: string,
      end: string
    ): Promise<void> {
      const {data} = await EventService.postContractChartDownload(token, {
        startDate: start,
        endDate: end,
        threshold: this.threshold,
      });
      saveAs(new Blob([data]), `トレーニング受講時間表_${start}_${end}.xlsx`);
    },

    checkIcon(runtime: number): string {
      if (runtime < this.threshold * 60) {
        return 'check_circle_outline';
      }
      return 'check_circle';
    },
    iconColor(runtime: number): string {
      if (runtime < this.threshold * 60) {
        return 'gray lighten-2';
      }
      return 'green darken-2';
    },
  },
  watch: {
    async start(newVal: string, oldVal: string) {
      if (newVal !== oldVal) {
        await this.onInputChange();
      }
    },
    async end(newVal: string, oldVal: string) {
      if (newVal !== oldVal) {
        await this.onInputChange();
      }
    },
  },
});
